import React, { useState, useEffect } from 'react';
import './LeaveRequest.css';
import collegeConfig from '../../config/collegeConfig';
import { FaCalendarAlt, FaArrowLeft, FaPlus, FaCheck, FaTimes, FaClock } from 'react-icons/fa';

const LeaveRequest = ({ onNavigate, parentUser }) => {
  const [view, setView] = useState('list'); // 'list' | 'form'
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [courseId, setCourseId] = useState('');

  const [form, setForm] = useState({
    dateFrom: '',
    dateTo: '',
    reason: ''
  });

  const token = localStorage.getItem('token');
  const user = parentUser || JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => {
    fetchCourseAndRequests();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchCourseAndRequests = async () => {
    try {
      setLoading(true);
      const userId = user.id || user._id;

      // Get enrolled course
      const studentRes = await fetch(`${collegeConfig.api.baseURL}/students/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (studentRes.ok) {
        const data = await studentRes.json();
        if (data.student?.course?._id) {
          setCourseId(data.student.course._id);
        }
      }

      // Get leave requests
      const res = await fetch(`${collegeConfig.api.baseURL}/leave/my-requests`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setRequests(data.requests || []);
      }
    } catch (err) {
      setError('Failed to load leave requests');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!form.dateFrom || !form.dateTo) {
      setError('Please fill in both dates');
      return;
    }
    if (new Date(form.dateFrom) > new Date(form.dateTo)) {
      setError('"From" date must be on or before "To" date');
      return;
    }
    if (!courseId) {
      setError('No enrolled course found. Please contact admin.');
      return;
    }

    try {
      setSubmitting(true);
      const res = await fetch(`${collegeConfig.api.baseURL}/leave`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          courseId,
          dateFrom: form.dateFrom,
          dateTo: form.dateTo,
          reason: form.reason || undefined
        })
      });

      const data = await res.json();
      if (data.success) {
        setSuccess('Leave request submitted successfully');
        setForm({ dateFrom: '', dateTo: '', reason: '' });
        setView('list');
        fetchCourseAndRequests();
      } else {
        setError(data.message || 'Failed to submit leave request');
      }
    } catch (err) {
      setError('Failed to submit leave request');
    } finally {
      setSubmitting(false);
    }
  };

  const statusBadge = (status) => {
    const map = {
      pending: { label: 'Pending', color: '#F39C12' },
      approved: { label: 'Approved', color: '#2ECC71' },
      rejected: { label: 'Rejected', color: '#E74C3C' }
    };
    const s = map[status] || map.pending;
    return (
      <span className="leave-status-badge" style={{ background: s.color }}>
        {status === 'pending' && <FaClock style={{ marginRight: 4 }} />}
        {status === 'approved' && <FaCheck style={{ marginRight: 4 }} />}
        {status === 'rejected' && <FaTimes style={{ marginRight: 4 }} />}
        {s.label}
      </span>
    );
  };

  const formatDate = (d) =>
    new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });

  return (
    <div className="leave-request-container">
      <div className="leave-header">
        <button className="leave-back-btn" onClick={() => onNavigate('dashboard')}>
          <FaArrowLeft /> Back
        </button>
        <h2><FaCalendarAlt style={{ marginRight: 8 }} />Leave Requests</h2>
        {view === 'list' && (
          <button className="leave-new-btn" onClick={() => { setError(''); setSuccess(''); setView('form'); }}>
            <FaPlus /> New Request
          </button>
        )}
      </div>

      {success && (
        <div className="leave-alert leave-alert-success">{success}</div>
      )}
      {error && (
        <div className="leave-alert leave-alert-error">{error}</div>
      )}

      {view === 'form' && (
        <div className="leave-form-card">
          <h3>Submit Leave Request</h3>
          <form onSubmit={handleSubmit} className="leave-form">
            <div className="leave-form-row">
              <div className="leave-form-group">
                <label>From Date <span className="required">*</span></label>
                <input
                  type="date"
                  value={form.dateFrom}
                  onChange={(e) => setForm(prev => ({ ...prev, dateFrom: e.target.value }))}
                  required
                />
              </div>
              <div className="leave-form-group">
                <label>To Date <span className="required">*</span></label>
                <input
                  type="date"
                  value={form.dateTo}
                  min={form.dateFrom}
                  onChange={(e) => setForm(prev => ({ ...prev, dateTo: e.target.value }))}
                  required
                />
              </div>
            </div>
            <div className="leave-form-group">
              <label>Reason <span className="optional">(optional)</span></label>
              <textarea
                value={form.reason}
                onChange={(e) => setForm(prev => ({ ...prev, reason: e.target.value }))}
                rows={3}
                placeholder="Enter reason for leave..."
              />
            </div>
            <div className="leave-form-actions">
              <button
                type="button"
                className="leave-btn leave-btn-secondary"
                onClick={() => { setView('list'); setError(''); }}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="leave-btn leave-btn-primary"
                disabled={submitting}
              >
                {submitting ? 'Submitting...' : 'Submit Request'}
              </button>
            </div>
          </form>
        </div>
      )}

      {view === 'list' && (
        <div className="leave-list">
          {loading ? (
            <div className="leave-loading">Loading...</div>
          ) : requests.length === 0 ? (
            <div className="leave-empty">
              <FaCalendarAlt className="leave-empty-icon" />
              <p>No leave requests yet.</p>
              <button className="leave-btn leave-btn-primary" onClick={() => setView('form')}>
                <FaPlus /> Submit a Request
              </button>
            </div>
          ) : (
            requests.map((req) => (
              <div key={req._id} className="leave-card">
                <div className="leave-card-header">
                  <div className="leave-dates">
                    <FaCalendarAlt className="leave-date-icon" />
                    <span>{formatDate(req.dateFrom)}</span>
                    <span className="leave-date-sep">—</span>
                    <span>{formatDate(req.dateTo)}</span>
                  </div>
                  {statusBadge(req.status)}
                </div>
                {req.reason && (
                  <p className="leave-reason">{req.reason}</p>
                )}
                {req.reviewNote && (
                  <p className="leave-review-note">
                    <strong>Teacher note:</strong> {req.reviewNote}
                  </p>
                )}
                <p className="leave-submitted-on">
                  Submitted {formatDate(req.createdAt)}
                </p>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default LeaveRequest;
